// Snap a dispatch-board drop onto an engineer row and a time slot.
// The slot size is 15, 30 or 60 minutes. The job keeps its own duration.

const SLOT_CHOICES = [15, 30, 60];

function normaliseSlotMinutes(value) {
  const minutes = Number(value);
  return SLOT_CHOICES.includes(minutes) ? minutes : 30;
}

function blockDurationHours(item, fallback = 1) {
  const raw = item && (item.duration_hours ?? item.estimated_hours);
  const hours = Number(raw);
  if (!Number.isFinite(hours) || hours <= 0) return fallback;
  return Math.round(hours * 100) / 100;
}

function snapMinutes(totalMinutes, slotMinutes, rangeStartHour, rangeEndHour) {
  const slot = normaliseSlotMinutes(slotMinutes);
  const start = Number(rangeStartHour) * 60;
  const end = Number(rangeEndHour) * 60;
  const last = Math.max(start, end - slot);
  const raw = Number(totalMinutes);
  const base = Number.isFinite(raw) ? raw : start;
  const snapped = Math.floor(base / slot) * slot;
  return Math.min(Math.max(snapped, start), last);
}

function snapToSlot({ startHour, startMinute, slotMinutes, rangeStart = 0, rangeEnd = 24 }) {
  const hour = Number(startHour);
  const minute = Number(startMinute || 0);
  let total;
  if (!Number.isFinite(hour)) total = Number(rangeStart) * 60;
  else if (!Number.isInteger(hour)) total = Math.round(hour * 60);
  else total = hour * 60 + (Number.isFinite(minute) ? minute : 0);
  const snapped = snapMinutes(total, slotMinutes, rangeStart, rangeEnd);
  return {
    start_hour: Math.floor(snapped / 60),
    start_minute: snapped % 60,
    start_minutes: snapped,
  };
}

function snapDrop({ clientX, laneLeft, laneWidth, dayStartHour, dayEndHour, slotMinutes }) {
  const width = Number(laneWidth) > 0 ? Number(laneWidth) : 1;
  const ratio = Math.min(1, Math.max(0, (Number(clientX) - Number(laneLeft)) / width));
  const start = Number(dayStartHour) * 60;
  const span = (Number(dayEndHour) - Number(dayStartHour)) * 60;
  const total = start + ratio * Math.max(span, 0);
  const capped = span > 0 ? Math.min(total, start + span - 0.001) : start;
  return snapMinutes(capped, slotMinutes, dayStartHour, dayEndHour);
}

function placementStyle(startMinutes, durationHours, dayStartHour, dayEndHour) {
  const span = (Number(dayEndHour) - Number(dayStartHour)) * 60;
  const safeSpan = span > 0 ? span : 1;
  const left = ((Number(startMinutes) - Number(dayStartHour) * 60) / safeSpan) * 100;
  const width = (Number(durationHours) * 60 / safeSpan) * 100;
  const clampedLeft = Math.min(Math.max(left, 0), 100);
  const clampedWidth = Math.max(0, Math.min(width, 100 - clampedLeft));
  return {
    left: `${clampedLeft}%`,
    width: `${clampedWidth}%`,
  };
}

function itemStartMinutes(item) {
  return Number(item.start_hour) * 60 + Number(item.start_minute || 0);
}

// Jobs that share a time go on separate rows. A job that only touches
// the next one (ends when the next starts) stays on the same row.
function packDayLanes(items) {
  const prepared = (items || []).map((item, index) => {
    const start = Number(item.startMinutes);
    const duration = Number(item.durationHours);
    const safeStart = Number.isFinite(start) ? start : 0;
    const safeDuration = Number.isFinite(duration) && duration > 0 ? duration : 0;
    return {
      key: String(item.key),
      start: safeStart,
      end: safeStart + safeDuration * 60,
      index,
    };
  });
  const sorted = prepared.slice().sort((a, b) => a.start - b.start || a.index - b.index);
  const result = {};
  let cluster = [];
  let clusterEnd = -Infinity;

  const flush = () => {
    if (!cluster.length) return;
    const laneEnds = [];
    const placed = [];
    const ordered = cluster.slice().sort((a, b) => a.start - b.start || a.end - b.end || a.index - b.index);
    ordered.forEach((item) => {
      let lane = 0;
      while (lane < laneEnds.length && laneEnds[lane] > item.start) lane += 1;
      laneEnds[lane] = item.end;
      placed.push({ key: item.key, lane });
    });
    placed.forEach((item) => {
      result[item.key] = { lane: item.lane, laneCount: laneEnds.length };
    });
    cluster = [];
    clusterEnd = -Infinity;
  };

  sorted.forEach((item) => {
    if (cluster.length && item.start >= clusterEnd) flush();
    cluster.push(item);
    clusterEnd = Math.max(clusterEnd, item.end);
  });
  flush();
  return result;
}

function quarterHours() {
  const times = [];
  for (let hour = 0; hour < 24; hour += 1) {
    for (let minute = 0; minute < 60; minute += 15) {
      times.push(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
    }
  }
  return times;
}

module.exports = {
  SLOT_CHOICES,
  normaliseSlotMinutes,
  blockDurationHours,
  snapMinutes,
  snapToSlot,
  snapDrop,
  placementStyle,
  itemStartMinutes,
  packDayLanes,
  quarterHours,
};
